import { ApplicationInsightsEventEnum } from "@/lib/enum/application-insights-event.enum";
import { restoreFromLocalStorage } from "@/lib/restoreFromLocalStorage";
import store from "@/store/VuexPlugin";
import { appInsights } from "@/lib/utility/app-insights";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { PartnerModule } from "./partner";

/**
 * Feature flags the user can toggle in the settings view.
 *
 * The whole module state is mirrored into `localStorage` by vuex-persist on every mutation, so a
 * persisted flag value is written for every user as soon as they use the app - whether or not they
 * ever touched the toggle. Flags are therefore stored as `boolean | null`, where `null` means "no
 * explicit choice" and the default lives in the getter (`this._flag ?? false`). Changing that
 * default then takes effect for everyone who never toggled the flag, while an explicit opt-out is
 * persisted as `false` and survives.
 *
 * Never inline a `true`/`false` default into `restoreFromLocalStorage` - it is resolved once at
 * construction time and frozen into `localStorage`, which makes later changes to it invisible to
 * existing users.
 */
@Module({
  dynamic: true,
  namespaced: true,
  name: "feature",
  store
})
export class FeatureStore extends VuexModule {
  @Action
  trackFeatureFlag({ flag, value }: { flag: string; value: boolean }) {
    appInsights.trackEvent(
      { name: ApplicationInsightsEventEnum.TOGGLE_FEATURE_FLAG },
      { partnerId: PartnerModule.partner?.id, flag, value }
    );
  }

  /**
   * Get the current value of the interactive camera setting.
   */
  get isInteractiveCameraEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the Pagination setting.
   */
  get isPaginationEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the progressStatusEnabled setting.
   */
  get isUpdateProgressStatusEnabled(): boolean {
    return true;
  }

  /**
   * True if settings control is enabled
   */
  get isPartnerManagerEnabled(): boolean {
    return true;
  }

  /**
   * True if partner analytics is enabled.
   */
  get isPartnerAnalyticsEnabled(): boolean {
    return true;
  }

  /**
   * True if multipartner analytics is enabled.
   */
  get isMultipartnerAnalyticsEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the isPdfUploadEnabled
   */
  get isPdfUploadEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the template setting.
   */
  get isTemplateEnabled(): boolean {
    return true;
  }

  get isContactsAsDriverEnabled(): boolean {
    return true;
  }

  _claimSettlementAnalyticsEnabled = false;

  get isClaimSettlementAnalyticsEnabled(): boolean {
    return this._claimSettlementAnalyticsEnabled;
  }

  @Mutation
  private _mutateClaimSettlementAnalyticsEnabled(value: boolean) {
    this._claimSettlementAnalyticsEnabled = value;
  }

  @Action
  public setIsClaimSettlementAnalyticsEnabled(value: boolean) {
    this.context.commit("_mutateClaimSettlementAnalyticsEnabled", value);
  }

  get isReportNewMobileView(): boolean {
    return true;
  }

  /**
   * Get the current value of the signature.
   */
  get isDigitalSignatureEnabled(): IDigitalSignatureFeature {
    const signatureFeature: IDigitalSignatureFeature = {
      isSignEnabled: true
    };
    return signatureFeature;
  }

  /**
   * Get the current value of the report.
   */
  get isReportEnabled(): IReportFeature {
    const report: IReportFeature = {
      isSharingEnabled: true,
      isTimelineEnabled: true
    };

    return report;
  }

  /**
   * Get the current default value of the marketplace.
   */
  get isMarketplaceEnabled(): IMarketplaceFeature {
    const isMarketplaceEnabled: IMarketplaceFeature = {
      isMarketplaceEnabled: true,
      isInsuranceCheckEnabled: true
    };

    return isMarketplaceEnabled;
  }

  /**
   * Setting for reportAdvertismentEnabled.
   */
  private _isReportAdvertisementEnabled: boolean | null = restoreFromLocalStorage(
    "_isReportAdvertisementEnabled",
    "feature",
    null
  );

  /**
   * Get the current value of the reportAdvertismentEnabled setting.
   */
  get isReportAdvertisementEnabled(): boolean {
    return this._isReportAdvertisementEnabled ?? false;
  }

  /**
   * Dispatches an action to set the report advetisement enabled property.
   *
   * @param value
   */
  @Action
  setReportAdvertisementEnabled(value: boolean) {
    this.context.commit("updateReportAdvertisementEnabled", value);
  }

  /**
   * Mutates the report advetisement enabled property.
   *
   * @param value
   */
  @Mutation
  updateReportAdvertisementEnabled(value: boolean) {
    this._isReportAdvertisementEnabled = value;
  }

  /**
   * Get the current value of the isInviteFriendsEnabled setting.
   */
  get isInviteFriendsEnabled(): boolean {
    return true;
  }

  /**
   * The renewable energy flag in the charging station form and the upload of proofs are always on.
   */
  get isRenewableChargingStationEnabled(): boolean {
    return true;
  }

  /**
   * Setting for partner colors
   */
  get isVuetifyInPartnerColors() {
    return PartnerModule.partner.settings?.isVuetifyInPartnerColors ?? false;
  }
  /**
   * Setting for partner colors
   */
  get isNavigationInPartnerColors() {
    return PartnerModule.partner.settings?.isNavigationInPartnerColors ?? false;
  }

  /**
   * Get the current value of the online booking feature flag.
   */
  get onlineBooking(): boolean {
    return true;
  }

  /**
   * Setting for upload attachments for online booking
   */
  private _attachmentsOnlineBooking: boolean | null = restoreFromLocalStorage(
    "_attachmentsOnlineBooking",
    "feature",
    null
  );

  get attachmentsOnlineBooking(): boolean {
    return this._attachmentsOnlineBooking ?? false;
  }

  @Mutation
  private _mutateSetAttachmentsOnlineBooking(value: boolean) {
    this._attachmentsOnlineBooking = value;
  }

  @Action
  public setAttachmentsOnlineBooking(value: boolean) {
    this.context.commit("_mutateSetAttachmentsOnlineBooking", value);
  }

  /**
   * Setting on billing page to check if the billing distribution is enabled
   */
  private _isBillingDistributionEnabled: boolean | null = restoreFromLocalStorage(
    "_isBillingDistributionEnabled",
    "feature",
    null
  );
  get isBillingDistributionEnabled(): boolean {
    return this._isBillingDistributionEnabled ?? false;
  }
  @Mutation
  private _mutateIsBillingDistributionEnabled(value: boolean) {
    this._isBillingDistributionEnabled = value;
  }
  @Action
  public setIsBillingDistributionEnabled(value: boolean) {
    this.context.commit("_mutateIsBillingDistributionEnabled", value);
  }
  /**
   * The billing overview for a partner is always on.
   */
  get isPartnerBillingOverviewEnabled(): boolean {
    return true;
  }

  /**
   * Uploading documents in the report is always on.
   */
  get isDocumentUploadEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the isInboxEnabled
   */
  get isInboxEnabled(): boolean {
    return true;
  }

  /**
   * Setting for the activity inbox: the toolbar button and side card listing the activities the
   * logged-in user received. Off by default while the feature is being rolled out.
   */
  private _isActivityInboxEnabled: boolean | null = restoreFromLocalStorage("_isActivityInboxEnabled", "feature", null);

  /**
   * Get the current value of the activity inbox setting
   */
  get isActivityInboxEnabled(): boolean {
    return this._isActivityInboxEnabled ?? false;
  }

  /**
   * Dispatches an action to show or hide the activity inbox
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsActivityInboxEnabled(value: boolean) {
    this.context.commit("_mutateIsActivityInboxEnabled", value);
  }

  /**
   * Mutates the activity inbox option
   *
   * @param value The value to set the option to.
   */
  @Mutation
  private _mutateIsActivityInboxEnabled(value: boolean) {
    this._isActivityInboxEnabled = value;
  }

  /**
   * Get the current value of option to enable new signature form
   */
  get isSignatureFormActive(): boolean {
    return true;
  }

  /**
   * Setting for showing option to enable ticket
   */
  private _isTicketEnabled: boolean | null = restoreFromLocalStorage("_isTicketEnabled", "feature", null);

  /**
   * Get the current value of option to enable  ticket
   */
  get isTicketEnabled(): boolean {
    return this._isTicketEnabled ?? false;
  }

  /**
   * Dispatches an action to enable  ticket
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsTicketEnabled(value: boolean) {
    this.context.commit("_mutateIsTicketEnabled", value);
  }

  /**
   * Mutates the option to use ticket
   *
   * @param value
   */
  @Mutation
  private _mutateIsTicketEnabled(value: boolean) {
    this._isTicketEnabled = value;
  }

  /**
   * Setting for showing option to enable ticket
   */
  private _isEvseEnabled: boolean | null = restoreFromLocalStorage("_isEvseEnabled", "feature", null);

  /**
   * Get the current value of option to enable  ticket
   */
  get isEvseEnabled(): boolean {
    return this._isEvseEnabled ?? false;
  }

  /**
   * Dispatches an action to enable  ticket
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsEvseEnabled(value: boolean) {
    this.context.commit("_mutateIsEvseEnabled", value);
  }

  /**
   * Mutates the option to use evse
   *
   * @param value
   */
  @Mutation
  private _mutateIsEvseEnabled(value: boolean) {
    this._isEvseEnabled = value;
  }

  /**
   * Setting for showing option to enable map
   */
  private _isMapShown: boolean | null = restoreFromLocalStorage("_isMapShown", "feature", null);

  /**
   * Get the current value of option to enable map
   */
  get isMapShown(): boolean {
    return this._isMapShown ?? false;
  }

  /**
   * Dispatches an action to enable map
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsMapShown(value: boolean) {
    this.context.commit("_mutateIsMapShown", value);
  }

  /**
   * Mutates the option to use evse
   *
   * @param value
   */
  @Mutation
  private _mutateIsMapShown(value: boolean) {
    this._isMapShown = value;
  }

  /**
   * Setting for showing option to hash email in gtm
   */
  private _isGtmHashEmail: boolean | null = restoreFromLocalStorage("_isGtmHashEmail", "feature", null);

  /**
   * Enables email hashing in GTM store
   */
  get isGtmHashEmail(): boolean {
    return this._isGtmHashEmail ?? false;
  }

  @Action
  public setIsGtmHashEmail(value: boolean) {
    this.context.commit("_mutateIsGtmHashEmail", value);
  }

  @Mutation
  private _mutateIsGtmHashEmail(value: boolean) {
    this._isGtmHashEmail = value;
  }

  get isCsvExport() {
    return true;
  }

  private _isQrCodeMultipleDownload = false;

  get isQrCodeMultipleDownload() {
    return this._isQrCodeMultipleDownload;
  }

  @Action
  public setIsQrCodeMultipleDownload(value: boolean) {
    this.context.commit("_mutateIsQrCodeMultipleDownload", value);
  }

  @Mutation
  private _mutateIsQrCodeMultipleDownload(value: boolean) {
    this._isQrCodeMultipleDownload = value;
  }

  /**
   * The extended acknowledgement dialog is always on.
   */
  get isExtendedAcknowledgement(): boolean {
    return true;
  }

  private _isNewVehicleView = false;

  get isNewVehicleView() {
    return this._isNewVehicleView;
  }

  @Action
  public setIsNewVehicleView(value: boolean) {
    this.context.commit("_mutateIsNewVehicleView", value);
  }

  @Mutation
  private _mutateIsNewVehicleView(value: boolean) {
    this._isNewVehicleView = value;
  }

  private _isFileSelectionOnShortPressInImageUpload: boolean | null = restoreFromLocalStorage(
    "_isFileSelectionOnShortPressInImageUpload",
    "feature",
    null
  );

  get isFileSelectionOnShortPressInImageUpload() {
    return this._isFileSelectionOnShortPressInImageUpload ?? false;
  }

  @Action
  public setIsFileSelectionOnShortPressInImageUpload(value: boolean) {
    this.context.commit("_mutateIsFileSelectionOnShortPressInImageUpload", value);
  }

  @Mutation
  private _mutateIsFileSelectionOnShortPressInImageUpload(value: boolean) {
    this._isFileSelectionOnShortPressInImageUpload = value;
  }

  get isTpsActive() {
    return true;
  }

  get isKsrAutoLoad() {
    return true;
  }

  private _isReportV2: boolean | null = restoreFromLocalStorage("_isReportV2", "feature", null);

  get isReportV2() {
    return this._isReportV2 ?? false;
  }

  @Action
  public setIsReportV2(value: boolean) {
    this.context.commit("_mutateIsReportV2", value);
  }

  @Mutation
  private _mutateIsReportV2(value: boolean) {
    this._isReportV2 = value;
  }

  get isTimeLine() {
    return true;
  }

  /**
   * The blueprint zoning step in the blueprint create and update dialogs is always on.
   */
  get isBlueprintZoningEnabled(): boolean {
    return true;
  }

  /**
   * Feature flag to use iframe player for videos (e.g., Mux Player).
   * Defaults to false so native <video> is used by default.
   */
  private _isVideoIframeEnabled: boolean | null = restoreFromLocalStorage("_isVideoIframeEnabled", "feature", null);

  /**
   * Returns true when iframe-based video playback is enabled.
   */
  get isVideoIframeEnabled(): boolean {
    return this._isVideoIframeEnabled ?? false;
  }

  @Mutation
  private _mutateIsVideoIframeEnabled(value: boolean) {
    this._isVideoIframeEnabled = value;
  }

  @Action
  public setIsVideoIframeEnabled(value: boolean) {
    this.context.commit("_mutateIsVideoIframeEnabled", value);
  }

  /**
   * Document V2 - the PDF viewer and the document detail views that replaced the iframe previews -
   * is always on.
   */
  get isDocumentV2Enabled(): boolean {
    return true;
  }

  /**
   * Feature flag for the new document view (Document V2) in the sign app.
   * Defaults to false so the sign app keeps using its previous document view.
   */
  private _isSignAppDocumentV2Enabled: boolean | null = restoreFromLocalStorage(
    "_isSignAppDocumentV2Enabled",
    "feature",
    null
  );

  /**
   * Returns true when the new document view (Document V2) is enabled in the sign app.
   */
  get isSignAppDocumentV2Enabled(): boolean {
    return this._isSignAppDocumentV2Enabled ?? false;
  }

  @Mutation
  private _mutateIsSignAppDocumentV2Enabled(value: boolean) {
    this._isSignAppDocumentV2Enabled = value;
  }

  @Action
  public setIsSignAppDocumentV2Enabled(value: boolean) {
    this.context.commit("_mutateIsSignAppDocumentV2Enabled", value);
  }

  /**
   * Tags on entities (repair-estimate, clearance-approval, appraisal, clearance, rental) are always on.
   */
  get isTagsEnabled(): boolean {
    return true;
  }

  /**
   * Feature flag for automatic registration recognition in workitem flow
   */
  private _isRegistrationRecognitionEnabled: boolean | null = restoreFromLocalStorage(
    "_isRegistrationRecognitionEnabled",
    "feature",
    null
  );

  get isRegistrationRecognitionEnabled(): boolean {
    return this._isRegistrationRecognitionEnabled ?? false;
  }

  @Mutation
  private _mutateIsRegistrationRecognitionEnabled(value: boolean) {
    this._isRegistrationRecognitionEnabled = value;
  }

  @Action
  public setIsRegistrationRecognitionEnabled(value: boolean) {
    this.context.commit("_mutateIsRegistrationRecognitionEnabled", value);
  }

  /**
   * Feature flag for event recipients (notify driver + recipients table rows).
   */
  private _isEventRecipientsEnabled: boolean | null = restoreFromLocalStorage(
    "_isEventRecipientsEnabled",
    "feature",
    null
  );

  get isEventRecipientsEnabled(): boolean {
    return this._isEventRecipientsEnabled ?? false;
  }

  @Mutation
  private _mutateIsEventRecipientsEnabled(value: boolean) {
    this._isEventRecipientsEnabled = value;
  }

  @Action
  public setIsEventRecipientsEnabled(value: boolean) {
    this.context.commit("_mutateIsEventRecipientsEnabled", value);
  }

  /**
   * Locking the tax rate on line items when the selected product has a tax rate configured
   * is always on.
   */
  get isProductTaxLockEnabled(): boolean {
    return true;
  }

  /**
   * Feature flag for the unified inbound-invoice import dialog (replaces the split
   * CreateInvoiceInboundDialog/ImportXInvoiceDialog entry points in InvoiceInboundTable.vue
   * while both dialogs still exist). Disabled by default.
   */
  private _isUnifiedInboundInvoiceEnabled: boolean | null = restoreFromLocalStorage(
    "_isUnifiedInboundInvoiceEnabled",
    "feature",
    null
  );

  get isUnifiedInboundInvoiceEnabled(): boolean {
    return this._isUnifiedInboundInvoiceEnabled ?? false;
  }

  @Mutation
  private _mutateIsUnifiedInboundInvoiceEnabled(value: boolean) {
    this._isUnifiedInboundInvoiceEnabled = value;
  }

  @Action
  public setIsUnifiedInboundInvoiceEnabled(value: boolean) {
    this.context.commit("_mutateIsUnifiedInboundInvoiceEnabled", value);
  }

  /**
   * The unified image and video gallery is always on.
   */
  get isUnifiedMediaGalleryEnabled(): boolean {
    return true;
  }

  /**
   * The HTML email editor and its template picker (MarkdownEditor-style) are always on.
   */
  get isHtmlEditorV2Enabled(): boolean {
    return true;
  }

  /**
   * The search-enabled message receiver field (person search, email type-ahead, recommended
   * receivers) is always on.
   */
  get isReceiverSearchEnabled(): boolean {
    return true;
  }

  /**
   * The reply / forward message actions in the message detail are always on.
   */
  get isReplyForwardEnabled(): boolean {
    return true;
  }

  /**
   * Feature flag for the expandable navigation sub groups in the side bar, e.g. the projects
   * below the `Projects` entry. Disabled by default; the entries stay flat links until a user
   * opts in.
   */
  private _isNavigationSubGroupsEnabled: boolean | null = restoreFromLocalStorage(
    "_isNavigationSubGroupsEnabled",
    "feature",
    null
  );

  get isNavigationSubGroupsEnabled(): boolean {
    return this._isNavigationSubGroupsEnabled ?? false;
  }

  @Mutation
  private _mutateIsNavigationSubGroupsEnabled(value: boolean) {
    this._isNavigationSubGroupsEnabled = value;
  }

  @Action
  public setIsNavigationSubGroupsEnabled(value: boolean) {
    const flag = "isNavigationSubGroupsEnabled";
    this.trackFeatureFlag({ flag, value });
    this.context.commit("_mutateIsNavigationSubGroupsEnabled", value);
  }

  /**
   * Feature flag for generating the DATEV billing exports (Buchungsstapel, DebKred Stamm) in the
   * backend instead of the browser. The backend reuses the DATEV factories of the THG module, so
   * the files carry the German DATEV headers and the ISO-8859-16 encoding the accounting imports.
   * Disabled by default; while off the browser keeps writing the UTF-8 CSV with English headers.
   */
  private _isInvoiceDatevBackendExportEnabled: boolean | null = restoreFromLocalStorage(
    "_isInvoiceDatevBackendExportEnabled",
    "feature",
    null
  );

  get isInvoiceDatevBackendExportEnabled(): boolean {
    return this._isInvoiceDatevBackendExportEnabled ?? false;
  }

  @Mutation
  private _mutateIsInvoiceDatevBackendExportEnabled(value: boolean) {
    this._isInvoiceDatevBackendExportEnabled = value;
  }

  @Action
  public setIsInvoiceDatevBackendExportEnabled(value: boolean) {
    const flag = "isInvoiceDatevBackendExportEnabled";
    this.trackFeatureFlag({ flag, value });
    this.context.commit("_mutateIsInvoiceDatevBackendExportEnabled", value);
  }

  /**
   * Feature flag for the walkaround rebuild of the handover tour: the blueprint band, the photo
   * stage beside the notes and the gallery overview, laid out for a tablet held in one hand.
   * Disabled by default, so the route keeps rendering the original tour view until a user opts in
   * and can go back to it by switching the flag off.
   */
  private _isHandoverTourV2Enabled: boolean | null = restoreFromLocalStorage(
    "_isHandoverTourV2Enabled",
    "feature",
    null
  );

  get isHandoverTourV2Enabled(): boolean {
    return this._isHandoverTourV2Enabled ?? false;
  }

  @Mutation
  private _mutateIsHandoverTourV2Enabled(value: boolean) {
    this._isHandoverTourV2Enabled = value;
  }

  @Action
  public setIsHandoverTourV2Enabled(value: boolean) {
    const flag = "isHandoverTourV2Enabled";
    this.trackFeatureFlag({ flag, value });
    this.context.commit("_mutateIsHandoverTourV2Enabled", value);
  }
}

export const FeatureModule = getModule(FeatureStore);

export interface IDigitalSignatureFeature {
  isSignEnabled: boolean;
}

export interface IReportFeature {
  isTimelineEnabled: boolean;
  isSharingEnabled: boolean;
}

export interface IMarketplaceFeature {
  isMarketplaceEnabled: boolean;
  isInsuranceCheckEnabled: boolean;
}
